// FancyTree.js
import React, { useState, useEffect, useCallback } from "react";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import {
  FaCheckSquare,
  FaSquare,
  FaChevronRight,
  FaChevronDown,
} from "react-icons/fa";

const FancyTree = ({
  data,
  onChange,
  initialChecked,
  disabled,
  mode = "feature",
}) => {
  const [treeData, setTreeData] = useState([]);
  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);

  useEffect(() => {
    // Ensure we're working with a clean array
    setChecked(Array.isArray(initialChecked) ? [...initialChecked] : []);
  }, [initialChecked]);

  // Build a map of all parent-child relationships
  const buildParentChildMap = useCallback((nodes, map = {}) => {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        map[node.value] = node.children.map(child => child.value);
        buildParentChildMap(node.children, map);
      }
    });
    return map;
  }, []);

  // Get all parent nodes
  const getAllParents = useCallback((nodes, parents = new Set()) => {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        parents.add(node.value);
        getAllParents(node.children, parents);
      }
    });
    return parents;
  }, []);

  // Transform raw data into tree nodes
  useEffect(() => {
    const transformData = (nodes) =>
      nodes.map((node) => ({
        value: mode === "entity" ? node.key : node.id.toString(),
        label: node.name,
        disabled,
        children: node.children ? transformData(node.children) : undefined,
      }));
    setTreeData(transformData(data));
  }, [data, mode, disabled]);

  const handleCheck = (checkedIds) => {
    const parentChildMap = buildParentChildMap(treeData);
    const allParents = getAllParents(treeData);

    let newChecked = [...checkedIds];

    // Add all parents that have at least one checked child
    allParents.forEach(parentId => {
      const children = parentChildMap[parentId] || [];
      const hasCheckedChild = children.some(childId => checkedIds.includes(childId));

      if (hasCheckedChild && !newChecked.includes(parentId)) {
        newChecked.push(parentId);
      }
    });

    // Remove parents that have no checked children
    allParents.forEach(parentId => {
      const children = parentChildMap[parentId] || [];
      const hasCheckedChild = children.some(childId => newChecked.includes(childId));

      if (!hasCheckedChild && newChecked.includes(parentId)) {
        newChecked = newChecked.filter(id => id !== parentId);
      }
    });

    // Remove duplicates
    newChecked = [...new Set(newChecked)];

    setChecked(newChecked);

    if (mode === "entity") {
      onChange(newChecked);
    } else {
      // Ensure we're only passing numbers for feature mode
      const numericValues = newChecked
        .map(item => {
          const num = Number(item);
          return isNaN(num) ? null : num;
        })
        .filter(item => item !== null);
      
      onChange(numericValues);
    }
  };

  return (
    <div className="fancy-tree-container">
      <CheckboxTree
        nodes={treeData}
        checked={checked}
        expanded={expanded}
        onCheck={handleCheck}
        onExpand={setExpanded}
        checkModel="all"
        icons={{
          check: (
            <FaCheckSquare className="rct-icon rct-icon-check white-icon" />
          ),
          uncheck: (
            <FaSquare className="rct-icon rct-icon-uncheck white-icon" />
          ),
          halfCheck: (
            <FaCheckSquare className="rct-icon rct-icon-half-check white-icon" />
          ),
          expandClose: (
            <FaChevronRight className="rct-icon rct-icon-expand-close white-icon" />
          ),
          expandOpen: (
            <FaChevronDown className="rct-icon rct-icon-expand-open white-icon" />
          ),
          parentClose: null,
          parentOpen: null,
          leaf: null,
        }}
      />
    </div>
  );
};

export default FancyTree;